import React, { useState, useEffect } from 'react';
import axios from 'axios';
import usePagination from '../../../utils/usePagination';
import Pagination from '../../../components/common/Pagination';

const API = process.env.REACT_APP_API_URL || 'https://sohaibenterprises.cloud/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const EMPTY = { buyer_name: '', phone: '', address: '', notes: '' };
const inputStyle = { width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };
const labelStyle = { fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '6px', display: 'block' };
const fmt = (n) => `Rs ${Number(n || 0).toLocaleString()}`;

const BuyersTab = () => {
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  const ledgerSales = ledger?.sales || [];
  const { paginatedData: paginatedSales, currentPage, setCurrentPage, totalPages } = usePagination(ledgerSales, 10);

  const fetchBuyers = () => {
    axios.get(`${API}/agriculture/buyers`, { headers: headers() })
      .then(r => { setBuyers(r.data.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchBuyers(); }, []);

  const openAdd = () => { setForm(EMPTY); setEditId(null); setShowForm(true); };
  const openEdit = (b) => {
    setForm({ buyer_name: b.buyer_name, phone: b.phone || '', address: b.address || '', notes: b.notes || '' });
    setEditId(b.id); setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.buyer_name) return alert('Buyer name is required');
    setSaving(true);
    try {
      if (editId) await axios.put(`${API}/agriculture/buyers/${editId}`, form, { headers: headers() });
      else await axios.post(`${API}/agriculture/buyers`, form, { headers: headers() });
      setShowForm(false); fetchBuyers();
      if (selectedBuyer) openLedger(selectedBuyer);
    } catch { alert('Failed to save'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this buyer? Their sales history will remain.')) return;
    await axios.delete(`${API}/agriculture/buyers/${id}`, { headers: headers() });
    fetchBuyers();
    if (selectedBuyer?.id === id) { setSelectedBuyer(null); setLedger(null); }
  };

  const openLedger = async (buyer) => {
    setSelectedBuyer(buyer);
    setLedgerLoading(true);
    try {
      const r = await axios.get(`${API}/agriculture/buyers/${buyer.id}/ledger`, { headers: headers() });
      setLedger(r.data.data);
    } catch { alert('Failed to load ledger'); }
    setLedgerLoading(false);
  };

  const updatePayment = async (saleId, status) => {
    try {
      await axios.patch(`${API}/agriculture/harvest-sales/${saleId}/payment`, { payment_status: status }, { headers: headers() });
      openLedger(selectedBuyer);
      fetchBuyers();
    } catch { alert('Failed to update payment'); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '60px', color: '#7f8c8d' }}>Loading...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#2c3e50', margin: 0 }}>Buyers</h2>
        <button onClick={openAdd} style={{ padding: '12px 24px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}>+ Add Buyer</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selectedBuyer ? '340px 1fr' : '1fr', gap: '24px' }}>

        {/* Buyers List */}
        <div>
          {buyers.length === 0 ? (
            <div style={{ background: 'white', borderRadius: '16px', padding: '40px', textAlign: 'center', color: '#7f8c8d', boxShadow: '0 4px 15px rgba(0,0,0,0.08)' }}>
              No buyers added yet. Click "+ Add Buyer" to start.
            </div>
          ) : buyers.map(b => (
            <div key={b.id} onClick={() => openLedger(b)} style={{
              background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)', cursor: 'pointer',
              border: selectedBuyer?.id === b.id ? '2px solid #27ae60' : '2px solid transparent',
              transition: 'all 0.2s'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: '#2c3e50' }}>{b.buyer_name}</div>
                  {b.phone && <div style={{ fontSize: '13px', color: '#7f8c8d', marginTop: '4px' }}>📞 {b.phone}</div>}
                  {b.address && <div style={{ fontSize: '13px', color: '#7f8c8d' }}>📍 {b.address}</div>}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '13px', color: '#7f8c8d' }}>{b.total_sales} sales</div>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: '#27ae60' }}>{fmt(b.total_received)}</div>
                  {parseFloat(b.balance_due) > 0 && (
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#e74c3c' }}>Due: {fmt(b.balance_due)}</div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }} onClick={e => e.stopPropagation()}>
                <button onClick={() => openEdit(b)} style={{ padding: '5px 12px', background: '#3498db', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>Edit</button>
                <button onClick={() => handleDelete(b.id)} style={{ padding: '5px 12px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>Delete</button>
              </div>
            </div>
          ))}
        </div>

        {/* Ledger Panel */}
        {selectedBuyer && (
          <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 15px rgba(0,0,0,0.08)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#2c3e50', margin: 0 }}>{selectedBuyer.buyer_name}</h3>
                <div style={{ fontSize: '13px', color: '#7f8c8d' }}>Transaction Ledger</div>
              </div>
              <button onClick={() => { setSelectedBuyer(null); setLedger(null); }} style={{ padding: '6px 14px', background: '#f0f0f0', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>✕ Close</button>
            </div>

            {ledgerLoading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>Loading ledger...</div>
            ) : ledger && (
              <>
                {/* Summary */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '24px' }}>
                  {[
                    { label: 'Total Sales', value: fmt(ledger.summary.total_amount), color: '#3498db' },
                    { label: 'Received', value: fmt(ledger.summary.total_received), color: '#27ae60' },
                    { label: 'Balance Due', value: fmt(ledger.summary.balance_due), color: ledger.summary.balance_due > 0 ? '#e74c3c' : '#27ae60' },
                  ].map(c => (
                    <div key={c.label} style={{ background: '#f8f9fa', borderRadius: '10px', padding: '14px', textAlign: 'center', borderLeft: `3px solid ${c.color}` }}>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: c.color }}>{c.value}</div>
                      <div style={{ fontSize: '12px', color: '#7f8c8d' }}>{c.label}</div>
                    </div>
                  ))}
                </div>

                {/* Transactions */}
                {ledger.sales.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>No sales recorded for this buyer yet.</div>
                ) : (
                  <>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#f8f9fa' }}>
                          {['Date', 'Crop', 'Qty (kg)', 'Amount', 'Status', 'Action'].map(h => (
                            <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#555', borderBottom: '2px solid #eee' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedSales.map((s, i) => (
                          <tr key={s.id} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                            <td style={{ padding: '10px 14px', color: '#555', fontSize: '13px' }}>{new Date(s.sale_date).toLocaleDateString('en-PK')}</td>
                            <td style={{ padding: '10px 14px', color: '#555', fontSize: '13px' }}>{s.crop_name || '—'}</td>
                            <td style={{ padding: '10px 14px', color: '#555', fontSize: '13px' }}>{Number(s.quantity_kg).toLocaleString()}</td>
                            <td style={{ padding: '10px 14px', fontWeight: '700', color: '#2c3e50', fontSize: '13px' }}>{fmt(s.total_amount)}</td>
                            <td style={{ padding: '10px 14px' }}>
                              <span style={{
                                padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600',
                                background: s.payment_status === 'received' ? '#27ae6020' : '#e74c3c20',
                                color: s.payment_status === 'received' ? '#27ae60' : '#e74c3c'
                              }}>{s.payment_status}</span>
                            </td>
                            <td style={{ padding: '10px 14px' }}>
                              {s.payment_status === 'pending' ? (
                                <button onClick={() => updatePayment(s.id, 'received')} style={{ padding: '4px 12px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
                                  Mark Received
                                </button>
                              ) : (
                                <button onClick={() => updatePayment(s.id, 'pending')} style={{ padding: '4px 12px', background: '#f39c12', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
                                  Mark Pending
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={setCurrentPage}
                      totalRecords={ledgerSales.length}
                      rowsPerPage={10}
                    />
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '480px' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#2c3e50', marginBottom: '24px' }}>{editId ? 'Edit Buyer' : 'Add New Buyer'}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={labelStyle}>Buyer Name *</label>
                <input style={inputStyle} value={form.buyer_name} onChange={e => setForm({ ...form, buyer_name: e.target.value })} placeholder="e.g. Arshad Trading Co." />
              </div>
              <div>
                <label style={labelStyle}>Phone</label>
                <input style={inputStyle} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="03xx-xxxxxxx" />
              </div>
              <div>
                <label style={labelStyle}>Address</label>
                <input style={inputStyle} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="City, area" />
              </div>
              <div>
                <label style={labelStyle}>Notes</label>
                <textarea style={{ ...inputStyle, height: '70px', resize: 'vertical' }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Any notes..." />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowForm(false)} style={{ padding: '12px 24px', background: '#f0f0f0', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ padding: '12px 24px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>
                {saving ? 'Saving...' : editId ? 'Update Buyer' : 'Add Buyer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuyersTab;